import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { useConferma } from "../components/ConfirmProvider";
import {
  FolderInput,
  Search,
  ExternalLink,
  Check,
  UserCheck,
  Trash2,
  UserPlus,
  Loader2,
} from "lucide-react";
// 29/09/2026 — date gg/mm/aaaa da un'unica fonte (d.data_arrivo era ISO "2026-09-29 13:54:43";
// la data di nascita letta dal codice fiscale era ISO YYYY-MM-DD).
import { formatDate, formatDateTime } from "../lib/format";

/**
 * Tappa 2 — "Documenti da assegnare".
 * I documenti arrivati senza un cliente riconosciuto restano in attesa qui:
 * si vede cosa dice il documento, si cerca il cliente e con un clic il file
 * si sposta nella sua cartella (in locale e su MEGA).
 *
 * 18/09/2026, richieste di Gianni:
 *  - tasto "Crea cliente" quando la persona letta sul documento non è in archivio
 *    (finestrella già compilata, il cliente lo crea SEMPRE lui premendo il tasto);
 *  - selezione multipla: si spuntano più documenti e si assegnano tutti insieme
 *    a un cliente (o si eliminano tutti insieme);
 *  - documenti raggruppati per persona letta, con "seleziona tutti" del gruppo.
 */
const ETICHETTE = {
  libretto: "Libretto di circolazione",
  carta_identita: "Carta d'identità",
  patente: "Patente",
  polizza: "Polizza",
  contratto: "Contratto",
  quietanza: "Quietanza",
  visura: "Visura",
  preventivo: "Preventivo",
  attestato: "Attestato di rischio",
  certificato: "Certificato",
  documento: "Documento",
  mandato: "Mandato",
};

const SENZA_NOME = "__senza_nome__";

const etichettaTipo = (tipo) => ETICHETTE[tipo] || tipo || "Documento";

// Una partita IVA (11 cifre) è un'azienda, un codice fiscale di 16 caratteri è un privato.
function sembraAzienda(cf) {
  const pulito = String(cf || "").replace(/[^0-9A-Za-z]/g, "");
  return pulito.length === 11 && /^[0-9]+$/.test(pulito);
}

export default function DocumentsToAssignPage() {
  const [items, setItems] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [ricerca, setRicerca] = useState({});
  const [risultati, setRisultati] = useState({});
  const [inCorso, setInCorso] = useState(null);
  const conferma = useConferma();

  // --- selezione multipla ---
  const [selezionati, setSelezionati] = useState([]); // document_id spuntati
  const [cercaCliente, setCercaCliente] = useState("");
  const [clientiTrovati, setClientiTrovati] = useState([]);
  const [lavoro, setLavoro] = useState(null); // { cosa, fatti, totale }

  // --- finestrella "crea cliente" ---
  const [creaAperto, setCreaAperto] = useState(false);
  const [creaPer, setCreaPer] = useState(null);
  const [creando, setCreando] = useState(false);
  const [letturaIA, setLetturaIA] = useState(false); // sto leggendo il documento con l'AI
  const [letti, setLetti] = useState([]); // campi che l'AI ha letto dal documento
  const [notaCf, setNotaCf] = useState(""); // nota sul codice fiscale (corretto / da controllare)
  const [statoCf, setStatoCf] = useState("");
  const [allegaFratelli, setAllegaFratelli] = useState(true); // allego anche gli altri suoi documenti
  const [creaForm, setCreaForm] = useState({
    client_type: "private",
    name: "",
    first_name: "",
    last_name: "",
    fiscal_code: "",
    birth_date: "",
    birth_place: "",
    birth_place_province: "",
    gender: "",
    address: "",
    city: "",
    cap: "",
    province: "",
    phone: "",
    email: "",
  });

  const carica = async () => {
    try {
      const r = await axios.get("/api/documents/unassigned");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error("Non riesco a leggere i documenti in attesa");
    } finally {
      setCaricamento(false);
    }
  };

  useEffect(() => {
    carica();
  }, []);

  // ---- raggruppamento per persona letta sul documento -------------------
  const gruppi = useMemo(() => {
    const mappa = new Map();
    for (const d of items) {
      const chiave = (d.persona || "").trim().toUpperCase() || SENZA_NOME;
      if (!mappa.has(chiave)) mappa.set(chiave, []);
      mappa.get(chiave).push(d);
    }
    return Array.from(mappa.entries())
      .map(([persona, docs]) => ({ persona, docs }))
      .sort((a, b) => {
        if (a.persona === SENZA_NOME) return 1;
        if (b.persona === SENZA_NOME) return -1;
        return b.docs.length - a.docs.length || a.persona.localeCompare(b.persona);
      });
  }, [items]);

  const nomeGruppo = (persona) => (persona === SENZA_NOME ? "Senza nome riconosciuto" : persona);

  // ---- selezione --------------------------------------------------------
  const toggleSelezione = (id) =>
    setSelezionati((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const selezionaTutti = () => setSelezionati(items.map((d) => d.document_id));
  const deselezionaTutti = () => setSelezionati([]);

  const toggleGruppo = (docs) => {
    const ids = docs.map((d) => d.document_id);
    const tuttiDentro = ids.every((id) => selezionati.includes(id));
    setSelezionati((prev) => {
      if (tuttiDentro) return prev.filter((id) => !ids.includes(id));
      return Array.from(new Set([...prev, ...ids]));
    });
  };

  const tuttiSelezionati = items.length > 0 && selezionati.length === items.length;

  // ---- ricerca cliente per un singolo documento -------------------------
  const cerca = async (docId, testo) => {
    setRicerca((p) => ({ ...p, [docId]: testo }));
    if (!testo || testo.trim().length < 2) {
      setRisultati((p) => ({ ...p, [docId]: [] }));
      return;
    }
    try {
      const r = await axios.get(`/api/clients?search=${encodeURIComponent(testo)}&limit=8`);
      setRisultati((p) => ({ ...p, [docId]: r.data.items || [] }));
    } catch (e) {
      setRisultati((p) => ({ ...p, [docId]: [] }));
    }
  };

  const assegna = async (docId, cliente) => {
    setInCorso(docId);
    try {
      const r = await axios.post(`/api/documents/${docId}/assign`, { client_id: cliente.client_id });
      const d = r.data || {};
      toast.success(
        `Documento assegnato a ${cliente.name}` +
          (d.agganciato_a === "polizza" ? ` (polizza ${d.polizza || ""})` : "") +
          (d.file_spostato ? " — file spostato" : "")
      );
      setItems((prev) => prev.filter((x) => x.document_id !== docId));
      setSelezionati((prev) => prev.filter((x) => x !== docId));
    } catch (e) {
      toast.error("Non assegnato: " + (e?.response?.data?.detail || e.message));
    } finally {
      setInCorso(null);
    }
  };

  // Documento che non interessa: si butta via.
  // L'eliminazione lo toglie dall'attesa, dal disco e da MEGA.
  const elimina = async (doc) => {
    const nome = doc.name || "questo documento";
    const ok = await conferma({
      titolo: "Eliminare questo documento?",
      descrizione: `"${nome}" verrà eliminato dall'elenco di attesa, dal disco e da MEGA. L'operazione non si può annullare.`,
      pericoloso: true,
    });
    if (!ok) return;
    setInCorso(doc.document_id);
    try {
      await axios.delete(`/api/documents/${doc.document_id}`);
      toast.success("Documento eliminato");
      setItems((prev) => prev.filter((x) => x.document_id !== doc.document_id));
      setSelezionati((prev) => prev.filter((x) => x !== doc.document_id));
    } catch (e) {
      toast.error("Non eliminato: " + (e?.response?.data?.detail || e.message));
    } finally {
      setInCorso(null);
    }
  };

  // ---- assegnazione in blocco ------------------------------------------
  const cercaPerBlocco = async (testo) => {
    setCercaCliente(testo);
    if (!testo || testo.trim().length < 2) {
      setClientiTrovati([]);
      return;
    }
    try {
      const r = await axios.get(`/api/clients?search=${encodeURIComponent(testo)}&limit=8`);
      setClientiTrovati(r.data.items || []);
    } catch (e) {
      setClientiTrovati([]);
    }
  };

  const assegnaSelezionati = async (cliente) => {
    const ids = items.filter((d) => selezionati.includes(d.document_id)).map((d) => d.document_id);
    if (ids.length === 0) return;
    const nomi = items.filter((d) => ids.includes(d.document_id)).map((d) => d.name);
    const ok = await conferma({
      titolo: `Assegnare ${ids.length} document${ids.length === 1 ? "o" : "i"} a ${cliente.name}?`,
      descrizione:
        "I file verranno spostati nella cartella del cliente (archivio e MEGA) e agganciati alla sua polizza quando la targa o il numero combaciano.",
      dettagli: (
        <ul className="list-disc pl-5">
          {nomi.slice(0, 12).map((n, i) => (
            <li key={i} className="font-mono text-xs">{n}</li>
          ))}
          {nomi.length > 12 ? <li className="text-xs">… e altri {nomi.length - 12}</li> : null}
        </ul>
      ),
      pericoloso: false,
    });
    if (!ok) return;

    setLavoro({ cosa: "assegno", fatti: 0, totale: ids.length });
    const falliti = [];
    let fatti = 0;
    for (const id of ids) {
      try {
        await axios.post(`/api/documents/${id}/assign`, { client_id: cliente.client_id });
        fatti += 1;
        setItems((prev) => prev.filter((x) => x.document_id !== id));
        setSelezionati((prev) => prev.filter((x) => x !== id));
      } catch (e) {
        falliti.push({ id, motivo: e?.response?.data?.detail || e.message });
      }
      setLavoro({ cosa: "assegno", fatti, totale: ids.length });
    }
    setLavoro(null);
    setCercaCliente("");
    setClientiTrovati([]);
    if (fatti) toast.success(`${fatti} document${fatti === 1 ? "o assegnato" : "i assegnati"} a ${cliente.name}`);
    if (falliti.length) {
      toast.error(`${falliti.length} non assegnat${falliti.length === 1 ? "o" : "i"}: ${falliti[0].motivo}`);
    }
  };

  const eliminaSelezionati = async () => {
    const docs = items.filter((d) => selezionati.includes(d.document_id));
    if (docs.length === 0) return;
    const ok = await conferma({
      titolo: `Eliminare ${docs.length} document${docs.length === 1 ? "o" : "i"}?`,
      descrizione:
        "Verranno eliminati dall'elenco di attesa, dal disco e da MEGA. L'operazione non si può annullare.",
      dettagli: (
        <ul className="list-disc pl-5">
          {docs.slice(0, 12).map((d, i) => (
            <li key={i} className="font-mono text-xs">
              {d.name} {d.persona ? `· ${d.persona}` : ""}
            </li>
          ))}
          {docs.length > 12 ? <li className="text-xs">… e altri {docs.length - 12}</li> : null}
        </ul>
      ),
      pericoloso: true,
      conferma: "Elimina",
    });
    if (!ok) return;

    setLavoro({ cosa: "elimino", fatti: 0, totale: docs.length });
    const falliti = [];
    let fatti = 0;
    for (const d of docs) {
      try {
        await axios.delete(`/api/documents/${d.document_id}`);
        fatti += 1;
        setItems((prev) => prev.filter((x) => x.document_id !== d.document_id));
        setSelezionati((prev) => prev.filter((x) => x !== d.document_id));
      } catch (e) {
        falliti.push({ id: d.document_id, motivo: e?.response?.data?.detail || e.message });
      }
      setLavoro({ cosa: "elimino", fatti, totale: docs.length });
    }
    setLavoro(null);
    if (fatti) toast.success(`${fatti} document${fatti === 1 ? "o eliminato" : "i eliminati"}`);
    if (falliti.length) {
      toast.error(`${falliti.length} non eliminat${falliti.length === 1 ? "o" : "i"}: ${falliti[0].motivo}`);
    }
  };

  // ---- creazione cliente dal documento ---------------------------------
  const apriCreaCliente = (doc) => {
    const cf = doc.codice_fiscale || "";
    const azienda = sembraAzienda(cf);
    setCreaPer(doc);
    setCreaForm({
      client_type: azienda ? "business" : "private",
      name: (doc.persona || "").toUpperCase(),
      first_name: "",
      last_name: "",
      fiscal_code: cf,
      birth_date: "",
      birth_place: "",
      birth_place_province: "",
      gender: "",
      address: "",
      city: "",
      cap: "",
      province: "",
      phone: "",
      email: "",
    });
    setAllegaFratelli(true);
    setLetti([]);
    setNotaCf("");
    setStatoCf("");
    setCreaAperto(true);
    if (!azienda && String(cf).replace(/[^0-9A-Za-z]/g, "").length === 16) {
      caricaDatiDaCF(cf);
    }
    // leggo il documento con l'AI e riempio tutto quello che c'e' scritto
    leggiDocumentoConAI(doc);
  };

  // Scarica il file del documento (se in locale manca, il server manda un link)
  const scaricaDocumento = async (doc) => {
    const r = await axios.get(`/api/documents/download/${doc.document_id}`, { responseType: "blob" });
    const blob = r.data;
    if ((blob?.type || "").includes("application/json")) {
      const j = JSON.parse(await blob.text());
      if (j?.redirect) {
        const r2 = await axios.get(j.redirect, { responseType: "blob" });
        return r2.data;
      }
    }
    return blob;
  };

  // Legge il documento con l'AI (Gemini) e compila la finestrella
  const leggiDocumentoConAI = async (doc) => {
    if (!doc?.document_id) return;
    setLetturaIA(true);
    try {
      const blob = await scaricaDocumento(doc);
      const nomeFile = doc.name || "documento";
      const ePdf = nomeFile.toLowerCase().endsWith(".pdf") || (doc.mime_type || "").includes("pdf");
      let dati = {};
      if (ePdf) {
        const fd = new FormData();
        fd.append("file", new File([blob], nomeFile, { type: "application/pdf" }));
        const res = await axios.post("/api/ai/parse-pdf?parse_type=client", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        dati = res.data?.parsed_data || {};
      } else {
        const b64 = await new Promise((resolve) => {
          const fr = new FileReader();
          fr.onload = () => resolve(String(fr.result || "").split(",")[1] || "");
          fr.readAsDataURL(blob);
        });
        const res = await axios.post("/api/ai/parse-image", { image: b64, type: "client" });
        dati = res.data?.parsed_data || {};
      }
      await applicaDatiLetti(dati, doc);
    } catch (e) {
      toast.warning("Non sono riuscito a leggere il documento: compila a mano i campi che mancano (o premi “Rileggi il documento”).");
    } finally {
      setLetturaIA(false);
    }
  };

  // Riempie la finestrella con quello che l'AI ha letto.
  // PRUDENZA: il modello locale ogni tanto legge male (dalla patente ha letto
  // "FI5819385U" al posto del codice fiscale, dalla carta d'identita' un cognome
  // diverso). Quindi:
  //   - se il codice fiscale ce l'abbiamo gia' dall'archivio NON si tocca;
  //   - un codice fiscale letto dall'AI si accetta solo se e' valido davvero;
  //   - nome e altri campi si riempiono solo se sono vuoti;
  //   - indirizzo, citta', CAP, provincia, telefono, email: questi si aggiungono,
  //     perche' sono i dati che l'archivio non ha.
  const applicaDatiLetti = async (dati, doc) => {
    const d = dati || {};
    const cfArchivio = (doc?.codice_fiscale || "").trim().toUpperCase();
    let cfAI = (d.fiscal_code || d.partita_iva || "").toUpperCase().replace(/\s/g, "");
    if (cfArchivio) {
      cfAI = ""; // quello dell'archivio e' piu' affidabile: non lo tocchiamo
    } else if (cfAI && cfAI.length === 16) {
      try {
        const v = await axios.get(`/api/clients/parse-fiscal-code/${cfAI}`);
        if (!v.data?.valid) cfAI = "";
      } catch (e) {
        cfAI = "";
      }
    } else if (cfAI && !sembraAzienda(cfAI)) {
      cfAI = "";
    }

    const letti = [];
    const segna = (campo, valore, etichetta) => {
      if (valore && !String(creaForm[campo] || "").trim()) letti.push(etichetta);
    };
    segna("address", d.address, "indirizzo");
    segna("city", d.city, "città");
    segna("cap", d.cap, "CAP");
    segna("province", d.province, "provincia");
    segna("phone", d.phone, "telefono");
    segna("email", d.email, "email");
    if (cfAI) letti.push("codice fiscale");

    setCreaForm((prev) => {
      const n = { ...prev };
      const metti = (campo, valore) => {
        if (valore && !String(n[campo] || "").trim()) n[campo] = valore;
      };
      if (d.name) metti("name", String(d.name).toUpperCase());
      if (cfAI) n.fiscal_code = cfAI;
      metti("address", (d.address || "").toUpperCase());
      metti("city", (d.city || "").toUpperCase());
      metti("cap", d.cap);
      metti("province", (d.province || "").toUpperCase());
      metti("phone", d.phone);
      metti("email", d.email);
      if (d.birth_date) metti("birth_date", d.birth_date);
      if (d.birth_place) metti("birth_place", String(d.birth_place).toUpperCase());
      if (d.gender) metti("gender", d.gender);
      if (sembraAzienda(n.fiscal_code)) n.client_type = "business";
      return n;
    });
    setLetti(letti);
    // l'app dice se il codice fiscale letto era sbagliato e se e' stato corretto
    const statoLetto = d.fiscal_code_stato || "";
    setStatoCf(statoLetto);
    setNotaCf(d.fiscal_code_nota || "");

    const cfDaUsare = cfAI || cfArchivio;
    if (cfDaUsare && cfDaUsare.length === 16 && !sembraAzienda(cfDaUsare)) caricaDatiDaCF(cfDaUsare);
  };

  // gli altri documenti in attesa della stessa persona (stesso codice fiscale, altrimenti stesso nome)
  const fratelli = useMemo(() => {
    if (!creaPer) return [];
    const cf = (creaPer.codice_fiscale || "").trim().toUpperCase();
    const persona = (creaPer.persona || "").trim().toUpperCase();
    return items.filter((d) => {
      if (d.document_id === creaPer.document_id) return false;
      const cfD = (d.codice_fiscale || "").trim().toUpperCase();
      if (cf && cfD) return cfD === cf;
      return !!persona && (d.persona || "").trim().toUpperCase() === persona;
    });
  }, [items, creaPer]);

  const caricaDatiDaCF = async (cf) => {
    try {
      const r = await axios.get(`/api/clients/parse-fiscal-code/${cf}`);
      if (r.data?.valid) {
        setCreaForm((prev) => ({
          ...prev,
          birth_date: r.data.birth_date || prev.birth_date,
          birth_place: r.data.birth_place || prev.birth_place,
          birth_place_province: r.data.estero ? "EE" : r.data.birth_place_province || prev.birth_place_province,
          gender: r.data.gender || prev.gender,
        }));
      }
    } catch (e) {
      /* se non riesce, i campi restano vuoti: si possono scrivere a mano */
    }
  };

  const creaClienteEAssegna = async () => {
    const nome = (creaForm.name || "").trim();
    if (!nome) {
      toast.error("Serve il nome (o la ragione sociale)");
      return;
    }
    setCreando(true);
    try {
      const corpo = {
        client_type: creaForm.client_type,
        name: nome.toUpperCase(),
        fiscal_code: (creaForm.fiscal_code || "").trim().toUpperCase() || null,
      };
      // indirizzo e contatti (valgono sia per i privati sia per le aziende)
      corpo.address = (creaForm.address || "").trim().toUpperCase() || null;
      corpo.city = (creaForm.city || "").trim().toUpperCase() || null;
      corpo.cap = (creaForm.cap || "").trim() || null;
      corpo.province = (creaForm.province || "").trim().toUpperCase() || null;
      corpo.phone = (creaForm.phone || "").trim() || null;
      corpo.email = (creaForm.email || "").trim() || null;
      if (creaForm.client_type === "private") {
        corpo.first_name = (creaForm.first_name || "").trim().toUpperCase() || null;
        corpo.last_name = (creaForm.last_name || "").trim().toUpperCase() || null;
        if (corpo.first_name && corpo.last_name) corpo.name_split_source = "manuale";
        corpo.birth_date = creaForm.birth_date || null;
        corpo.birth_place = creaForm.birth_place || null;
        corpo.birth_place_province = creaForm.birth_place_province || null;
        corpo.gender = creaForm.gender || null;
      }
      const r = await axios.post("/api/clients", corpo);
      const nuovo = r.data || {};
      if (!nuovo.client_id) throw new Error("cliente non creato");

      // assegno il documento di partenza e, se vuole, anche gli altri della stessa persona
      const daAssegnare = [];
      if (creaPer?.document_id) daAssegnare.push(creaPer.document_id);
      if (allegaFratelli) fratelli.forEach((d) => daAssegnare.push(d.document_id));
      let assegnati = 0;
      const nonAssegnati = [];
      for (const id of daAssegnare) {
        try {
          await axios.post(`/api/documents/${id}/assign`, { client_id: nuovo.client_id });
          assegnati += 1;
          setItems((prev) => prev.filter((x) => x.document_id !== id));
          setSelezionati((prev) => prev.filter((x) => x !== id));
        } catch (e) {
          nonAssegnati.push(e?.response?.data?.detail || e.message);
        }
      }
      toast.success(
        `Cliente ${nuovo.name || nome} creato` +
          (assegnati ? ` · ${assegnati} document${assegnati === 1 ? "o assegnato" : "i assegnati"}` : "")
      );
      if (nonAssegnati.length) toast.error(`${nonAssegnati.length} documenti non assegnati: ${nonAssegnati[0]}`);
      setCreaAperto(false);
      setCreaPer(null);
    } catch (e) {
      toast.error("Cliente non creato: " + (e?.response?.data?.detail || e.message));
    } finally {
      setCreando(false);
    }
  };

  const barraLavoro = lavoro
    ? `${lavoro.cosa === "assegno" ? "Assegno" : "Elimino"} ${lavoro.fatti + 1} di ${lavoro.totale}…`
    : null;

  return (
    <div className="space-y-6" data-testid="documents-to-assign-page">
      <PageHeader
        titolo="Documenti da assegnare"
        descrizione="Documenti arrivati senza un cliente riconosciuto: scegli a chi appartengono e il file va nella sua cartella. Puoi spuntarne più di uno e assegnarli (o eliminarli) tutti insieme."
        icona={<FolderInput className="h-7 w-7 text-primary" />}
      />

      {caricamento ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">Carico…</CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center space-y-2" data-testid="documents-to-assign-empty">
            <Check className="h-10 w-10 mx-auto text-green-600" />
            <p className="font-medium">Nessun documento in attesa</p>
            <p className="text-sm text-muted-foreground">
              Tutti i documenti hanno un cliente. Quando ne arriva uno che Sara non riconosce, lo trovi qui.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-muted-foreground" data-testid="documents-to-assign-count">
              <span className="font-medium">{items.length}</span>{" "}
              {items.length === 1 ? "documento in attesa" : "documenti in attesa"}
              {gruppi.length > 1 ? ` · ${gruppi.length} persone/gruppi` : ""}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={tuttiSelezionati ? deselezionaTutti : selezionaTutti}
              data-testid="select-all-documents"
            >
              {tuttiSelezionati ? "Deseleziona tutti" : "Seleziona tutti"}
            </Button>
          </div>

          {/* Barra dei comandi per la selezione multipla */}
          {selezionati.length > 0 && (
            <div
              className="sticky top-2 z-20 rounded-lg border bg-card/95 backdrop-blur p-3 space-y-3 shadow-sm"
              data-testid="selection-bar"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Badge data-testid="selection-count">{selezionati.length} selezionati</Badge>
                <Button variant="ghost" size="sm" onClick={deselezionaTutti} data-testid="selection-clear">
                  Svuota selezione
                </Button>
                {barraLavoro ? (
                  <span className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="selection-working">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {barraLavoro}
                  </span>
                ) : null}
              </div>
              <div className="flex flex-col lg:flex-row gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    className="pl-9"
                    placeholder={`Cerca il cliente a cui assegnare i ${selezionati.length} documenti…`}
                    value={cercaCliente}
                    onChange={(e) => cercaPerBlocco(e.target.value)}
                    disabled={!!lavoro}
                    data-testid="batch-assign-search"
                  />
                </div>
                <Button
                  variant="outline"
                  className="text-red-600 hover:text-red-700"
                  onClick={eliminaSelezionati}
                  disabled={!!lavoro}
                  data-testid="batch-delete"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Elimina i selezionati
                </Button>
              </div>
              {clientiTrovati.length > 0 && (
                <div className="rounded-md border divide-y" data-testid="batch-assign-results">
                  {clientiTrovati.map((c) => (
                    <div key={c.client_id} className="flex items-center justify-between gap-2 px-3 py-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{c.name}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {[c.fiscal_code, c.city].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => assegnaSelezionati(c)}
                        disabled={!!lavoro}
                        data-testid={`batch-assign-${c.client_id}`}
                      >
                        <UserCheck className="mr-2 h-4 w-4" />
                        Assegna {selezionati.length}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              {cercaCliente.trim().length >= 2 && clientiTrovati.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Nessun cliente trovato con “{cercaCliente}”.
                </p>
              ) : null}
            </div>
          )}

          {/* Documenti raggruppati per persona letta sul documento */}
          <div className="space-y-6">
            {gruppi.map((g) => {
              const idsGruppo = g.docs.map((d) => d.document_id);
              const gruppoIntero = idsGruppo.every((id) => selezionati.includes(id));
              return (
                <div key={g.persona} className="space-y-3" data-testid={`group-${g.persona}`}>
                  <div className="flex flex-wrap items-center gap-2 border-b pb-1">
                    <Checkbox
                      checked={gruppoIntero}
                      onCheckedChange={() => toggleGruppo(g.docs)}
                      aria-label={`Seleziona tutti i documenti di ${nomeGruppo(g.persona)}`}
                      data-testid={`group-select-${g.persona}`}
                    />
                    <span className="font-medium text-sm">{nomeGruppo(g.persona)}</span>
                    <Badge variant="secondary" className="text-xs">
                      {g.docs.length} {g.docs.length === 1 ? "documento" : "documenti"}
                    </Badge>
                    {g.docs.length > 1 ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="ml-auto"
                        onClick={() => apriCreaCliente(g.docs.find((x) => x.codice_fiscale) || g.docs[0])}
                        title="Crea il cliente e assegna tutti i documenti di questa persona"
                        data-testid={`group-create-client-${g.persona}`}
                      >
                        <UserPlus className="mr-2 h-4 w-4" />
                        Crea cliente e assegna tutti ({g.docs.length})
                      </Button>
                    ) : null}
                  </div>

                  {g.docs.map((d) => (
                    <Card key={d.document_id} data-testid={`unassigned-${d.document_id}`}>
                      <CardContent className="p-4 space-y-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <Checkbox
                            checked={selezionati.includes(d.document_id)}
                            onCheckedChange={() => toggleSelezione(d.document_id)}
                            aria-label={`Seleziona ${d.name}`}
                            data-testid={`select-doc-${d.document_id}`}
                          />
                          <Badge variant="secondary">{etichettaTipo(d.tipo)}</Badge>
                          {d.targa && <Badge variant="outline">targa {d.targa}</Badge>}
                          {d.numero_polizza && <Badge variant="outline">polizza {d.numero_polizza}</Badge>}
                          {!d.file_locale && <Badge variant="destructive">file non trovato</Badge>}
                          <span className="text-xs text-muted-foreground ml-auto">{formatDateTime(d.data_arrivo)}</span>
                        </div>

                        <div className="text-sm space-y-0.5">
                          {(d.persona || d.codice_fiscale) && (
                            <div>
                              <span className="text-muted-foreground">Letto dal documento: </span>
                              <span className="font-medium">{d.persona || "—"}</span>
                              {d.codice_fiscale ? (
                                <span className="text-muted-foreground"> · {d.codice_fiscale}</span>
                              ) : null}
                              {d.codice_fiscale_nota ? (
                                <div
                                  className={
                                    d.codice_fiscale_stato === "corretto"
                                      ? "text-xs text-amber-600"
                                      : "text-xs text-red-600"
                                  }
                                  data-testid={`cf-nota-${d.document_id}`}
                                >
                                  {d.codice_fiscale_stato === "corretto"
                                    ? "codice fiscale corretto in automatico"
                                    : "codice fiscale da controllare a mano"}
                                </div>
                              ) : null}
                            </div>
                          )}
                          {d.riassunto && (
                            <div className="text-muted-foreground line-clamp-2">
                              {String(d.riassunto).slice(0, 240)}
                            </div>
                          )}
                          <div className="text-xs text-muted-foreground font-mono">{d.name}</div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                          <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              className="pl-9"
                              placeholder="Cerca il cliente (nome, cognome o codice fiscale)…"
                              value={ricerca[d.document_id] || ""}
                              onChange={(e) => cerca(d.document_id, e.target.value)}
                              data-testid={`assign-search-${d.document_id}`}
                            />
                          </div>
                          {d.file_locale && (
                            <Button variant="outline" asChild>
                              <a
                                href={`/api/documents/download/${d.document_id}`}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`assign-open-${d.document_id}`}
                              >
                                <ExternalLink className="mr-2 h-4 w-4" />
                                Apri
                              </a>
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => elimina(d)}
                            disabled={inCorso === d.document_id}
                            title="Elimina il documento (dal disco e da MEGA)"
                            data-testid={`assign-delete-${d.document_id}`}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Elimina
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => apriCreaCliente(d)}
                            disabled={inCorso === d.document_id}
                            title="Il cliente non è in archivio: crealo con i dati letti sul documento"
                            data-testid={`create-client-${d.document_id}`}
                          >
                            <UserPlus className="mr-2 h-4 w-4" />
                            Crea cliente
                          </Button>
                        </div>

                        {(risultati[d.document_id] || []).length > 0 && (
                          <div className="rounded-md border divide-y">
                            {(risultati[d.document_id] || []).map((c) => (
                              <div key={c.client_id} className="flex items-center justify-between gap-2 px-3 py-2">
                                <div className="min-w-0">
                                  <div className="text-sm font-medium truncate">{c.name}</div>
                                  <div className="text-xs text-muted-foreground truncate">
                                    {[c.fiscal_code, c.city].filter(Boolean).join(" · ")}
                                  </div>
                                </div>
                                <Button
                                  size="sm"
                                  onClick={() => assegna(d.document_id, c)}
                                  disabled={inCorso === d.document_id}
                                  data-testid={`assign-${d.document_id}-${c.client_id}`}
                                >
                                  <UserCheck className="mr-2 h-4 w-4" />
                                  {inCorso === d.document_id ? "Assegno…" : "Assegna"}
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Se la ricerca non trova nessuno, lo diciamo (il tasto "Crea cliente" è qui sopra) */}
                        {(ricerca[d.document_id] || "").trim().length >= 2 &&
                        (risultati[d.document_id] || []).length === 0 ? (
                          <p className="text-sm text-muted-foreground" data-testid={`no-client-${d.document_id}`}>
                            Nessun cliente trovato con “{ricerca[d.document_id]}”: se è una persona nuova usa “Crea
                            cliente”.
                          </p>
                        ) : null}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Finestrella di creazione cliente, già compilata con quello che dice il documento */}
      <Dialog open={creaAperto} onOpenChange={(v) => !creando && setCreaAperto(v)}>
        <DialogContent className="max-w-lg" data-testid="create-client-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Crea cliente
            </DialogTitle>
            <DialogDescription>
              Dati già compilati con quello che si legge sul documento{creaPer ? ` (${creaPer.name})` : ""}. Controlla e correggi
              quello che serve, poi il documento viene assegnato al nuovo cliente.
            </DialogDescription>
            {letturaIA ? (
              <p className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="create-client-reading">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Sto leggendo il documento con l'AI e compilo i campi…
              </p>
            ) : null}
            {!letturaIA && letti.length > 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="create-client-read">
                Letto dal documento: {letti.join(", ")}.
              </p>
            ) : null}
            {!letturaIA && notaCf ? (
              <p
                className={statoCf === "corretto" ? "text-xs text-amber-600" : "text-xs text-red-600"}
                data-testid="create-client-cf-note"
              >
                {statoCf === "corretto"
                  ? "Il codice fiscale letto dal documento non era valido: l'ho corretto in automatico. Controlla."
                  : "Il codice fiscale letto non supera il controllo: controllalo a mano."}
              </p>
            ) : null}
            {!letturaIA && creaPer ? (
              <Button
                variant="ghost"
                size="sm"
                className="self-start h-7 px-2 text-xs"
                onClick={() => leggiDocumentoConAI(creaPer)}
                data-testid="create-client-reread"
              >
                Rileggi il documento
              </Button>
            ) : null}
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="crea-tipo">Tipo</Label>
              <Select
                value={creaForm.client_type}
                onValueChange={(v) => setCreaForm((p) => ({ ...p, client_type: v }))}
              >
                <SelectTrigger id="crea-tipo" data-testid="create-client-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Privato</SelectItem>
                  <SelectItem value="business">Azienda</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="crea-nome">
                {creaForm.client_type === "business" ? "Ragione sociale" : "Nome e cognome (come deve apparire)"}
              </Label>
              <Input
                id="crea-nome"
                value={creaForm.name}
                onChange={(e) => setCreaForm((p) => ({ ...p, name: e.target.value }))}
                data-testid="create-client-name"
              />
            </div>

            {creaForm.client_type === "private" && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="crea-cognome">Cognome (facoltativo)</Label>
                  <Input
                    id="crea-cognome"
                    value={creaForm.last_name}
                    onChange={(e) => setCreaForm((p) => ({ ...p, last_name: e.target.value }))}
                    data-testid="create-client-lastname"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="crea-nome2">Nome (facoltativo)</Label>
                  <Input
                    id="crea-nome2"
                    value={creaForm.first_name}
                    onChange={(e) => setCreaForm((p) => ({ ...p, first_name: e.target.value }))}
                    data-testid="create-client-firstname"
                  />
                </div>
                <p className="col-span-2 text-xs text-muted-foreground -mt-1">
                  Cognome e nome servono per l'ordinamento e la ricerca: dividili tu, così non sbaglio l'ordine letto sul documento.
                </p>
              </div>
            )}

            <div className="space-y-1">
              <Label htmlFor="crea-cf">{creaForm.client_type === "business" ? "Partita IVA" : "Codice fiscale"}</Label>
              <Input
                id="crea-cf"
                value={creaForm.fiscal_code}
                onChange={(e) => {
                  const v = e.target.value.toUpperCase();
                  setCreaForm((p) => ({ ...p, fiscal_code: v }));
                  if (v.replace(/[^0-9A-Za-z]/g, "").length === 16 && creaForm.client_type === "private") {
                    caricaDatiDaCF(v);
                  }
                }}
                data-testid="create-client-cf"
              />
              {creaForm.birth_date || creaForm.birth_place ? (
                <p className="text-xs text-muted-foreground" data-testid="create-client-cfdata">
                  Dal codice fiscale: {creaForm.birth_date ? formatDate(creaForm.birth_date) : "?"} · {creaForm.birth_place || "?"}
                  {creaForm.birth_place_province ? ` (${creaForm.birth_place_province})` : ""}
                  {creaForm.gender ? ` · ${creaForm.gender}` : ""}
                </p>
              ) : null}
            </div>
          </div>

          {/* Indirizzo e contatti: spesso sono scritti sul documento */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="crea-indirizzo">Indirizzo</Label>
              <Input
                id="crea-indirizzo"
                value={creaForm.address}
                onChange={(e) => setCreaForm((p) => ({ ...p, address: e.target.value }))}
                data-testid="create-client-address"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="crea-cap">CAP</Label>
              <Input
                id="crea-cap"
                value={creaForm.cap}
                onChange={(e) => setCreaForm((p) => ({ ...p, cap: e.target.value }))}
                data-testid="create-client-cap"
              />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="crea-citta">Città</Label>
              <Input
                id="crea-citta"
                value={creaForm.city}
                onChange={(e) => setCreaForm((p) => ({ ...p, city: e.target.value }))}
                data-testid="create-client-city"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="crea-provincia">Provincia</Label>
              <Input
                id="crea-provincia"
                maxLength={2}
                value={creaForm.province}
                onChange={(e) => setCreaForm((p) => ({ ...p, province: e.target.value.toUpperCase() }))}
                data-testid="create-client-province"
              />
            </div>
            <div className="space-y-1 sm:col-span-3">
              <Label htmlFor="crea-telefono">Telefono</Label>
              <Input
                id="crea-telefono"
                value={creaForm.phone}
                onChange={(e) => setCreaForm((p) => ({ ...p, phone: e.target.value }))}
                data-testid="create-client-phone"
              />
            </div>
            <div className="space-y-1 sm:col-span-3">
              <Label htmlFor="crea-email">Email</Label>
              <Input
                id="crea-email"
                value={creaForm.email}
                onChange={(e) => setCreaForm((p) => ({ ...p, email: e.target.value }))}
                data-testid="create-client-email"
              />
            </div>
          </div>

          {/* Altri documenti della stessa persona già in attesa */}
          {fratelli.length > 0 ? (
            <div
              className="flex items-start gap-2 rounded-md border border-dashed p-3"
              data-testid="create-client-siblings"
            >
              <Checkbox
                checked={allegaFratelli}
                onCheckedChange={(v) => setAllegaFratelli(!!v)}
                aria-label="Assegna anche gli altri documenti di questa persona"
                data-testid="create-client-siblings-check"
              />
              <div className="text-sm">
                <div className="font-medium">
                  Assegna anche gli altri {fratelli.length} documenti di questa persona
                </div>
                <div className="text-xs text-muted-foreground">{fratelli.map((d) => d.name).join(", ")}</div>
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreaAperto(false)} disabled={creando} data-testid="create-client-cancel">
              Annulla
            </Button>
            <Button onClick={creaClienteEAssegna} disabled={creando} data-testid="create-client-submit">
              {creando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}
              {creando ? "Creo…" : "Crea e assegna"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
